import { useCallback, useMemo } from 'react'
import { View, Text, TextInput, Pressable, Platform } from 'react-native'
import { Search } from 'lucide-react-native'
import { useTheme } from '../../../theme/useTheme'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import {
  ACCOUNT_MEMBERS_DIRECTORY_TEST_IDS,
  MEMBERS_DIRECTORY_ROLE_FILTER,
  MEMBERS_DIRECTORY_SORT,
  type MembersDirectoryRoleFilter,
  type MembersDirectorySortOption,
} from '../../constants/accountMembersDirectory.constants'
import { shouldShowMembersDirectoryResultsSummary } from '../../helpers/b2bMembersDirectory.helper'
import { getAccountMembersDirectoryControlsStyles } from './AccountMembersDirectoryControls.styles'

interface AccountMembersDirectoryControlsProps {
  searchQuery: string
  onSearchQueryChange: (value: string) => void
  roleFilter: MembersDirectoryRoleFilter
  onRoleFilterChange: (value: MembersDirectoryRoleFilter) => void
  sortOption: MembersDirectorySortOption
  onSortOptionChange: (value: MembersDirectorySortOption) => void
  shownCount: number
  totalCount: number
  disabled?: boolean
}

interface DirectoryChipProps {
  label: string
  active: boolean
  onPress: () => void
  testID: string
  disabled: boolean
  styles: ReturnType<typeof getAccountMembersDirectoryControlsStyles>
}

function DirectoryChip({
  label,
  active,
  onPress,
  testID,
  disabled,
  styles,
}: DirectoryChipProps) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: active, disabled }}
      testID={testID}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed, hovered }) => [
        styles.chip,
        active && styles.chipActive,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressed && !disabled && styles.chipPressed,
        hovered && Platform.OS === 'web' && !disabled && styles.chipHover,
        disabled && styles.chipDisabled,
      ]}
    >
      <Text style={[styles.chipText, active && styles.chipTextActive]}>{label}</Text>
    </Pressable>
  )
}

export function AccountMembersDirectoryControls({
  searchQuery,
  onSearchQueryChange,
  roleFilter,
  onRoleFilterChange,
  sortOption,
  onSortOptionChange,
  shownCount,
  totalCount,
  disabled = false,
}: AccountMembersDirectoryControlsProps) {
  const copy = useAccountAdminCopy()
  const theme = useTheme()
  const styles = useMemo(() => getAccountMembersDirectoryControlsStyles(theme), [theme])

  const handleSearchChange = useCallback(
    (text: string) => {
      onSearchQueryChange(text)
    },
    [onSearchQueryChange],
  )

  const showResultsSummary = shouldShowMembersDirectoryResultsSummary(
    searchQuery,
    shownCount,
    totalCount,
  )

  return (
    <View style={styles.controlsBlock}>
      <View style={styles.searchRow}>
        <View style={styles.searchIconWrap}>
          <Search size={18} color={theme.gray[500]} strokeWidth={2} />
        </View>
        <TextInput
          testID={ACCOUNT_MEMBERS_DIRECTORY_TEST_IDS.SEARCH_INPUT}
          accessibilityLabel={copy.MEMBERS_DIRECTORY_SEARCH_A11Y}
          value={searchQuery}
          onChangeText={handleSearchChange}
          placeholder={copy.MEMBERS_DIRECTORY_SEARCH_PLACEHOLDER}
          placeholderTextColor={theme.gray[500]}
          editable={!disabled}
          autoCapitalize="none"
          autoCorrect={false}
          style={styles.searchInput}
        />
      </View>

      <View style={styles.filterRow}>
        <DirectoryChip
          label={copy.MEMBERS_DIRECTORY_FILTER_ALL}
          active={roleFilter === MEMBERS_DIRECTORY_ROLE_FILTER.ALL}
          onPress={() => onRoleFilterChange(MEMBERS_DIRECTORY_ROLE_FILTER.ALL)}
          testID={ACCOUNT_MEMBERS_DIRECTORY_TEST_IDS.FILTER_ALL}
          disabled={disabled}
          styles={styles}
        />
        <DirectoryChip
          label={copy.MEMBERS_DIRECTORY_FILTER_ADMIN}
          active={roleFilter === MEMBERS_DIRECTORY_ROLE_FILTER.ADMIN}
          onPress={() => onRoleFilterChange(MEMBERS_DIRECTORY_ROLE_FILTER.ADMIN)}
          testID={ACCOUNT_MEMBERS_DIRECTORY_TEST_IDS.FILTER_ADMIN}
          disabled={disabled}
          styles={styles}
        />
        <DirectoryChip
          label={copy.MEMBERS_DIRECTORY_FILTER_MEMBER}
          active={roleFilter === MEMBERS_DIRECTORY_ROLE_FILTER.MEMBER}
          onPress={() => onRoleFilterChange(MEMBERS_DIRECTORY_ROLE_FILTER.MEMBER)}
          testID={ACCOUNT_MEMBERS_DIRECTORY_TEST_IDS.FILTER_MEMBER}
          disabled={disabled}
          styles={styles}
        />
      </View>

      <View style={styles.sortRow}>
        <Text style={styles.sortLabel}>{copy.MEMBERS_DIRECTORY_SORT_LABEL}</Text>
        <DirectoryChip
          label={copy.MEMBERS_DIRECTORY_SORT_DEFAULT}
          active={sortOption === MEMBERS_DIRECTORY_SORT.DEFAULT}
          onPress={() => onSortOptionChange(MEMBERS_DIRECTORY_SORT.DEFAULT)}
          testID={ACCOUNT_MEMBERS_DIRECTORY_TEST_IDS.SORT_DEFAULT}
          disabled={disabled}
          styles={styles}
        />
        <DirectoryChip
          label={copy.MEMBERS_DIRECTORY_SORT_NAME_ASC}
          active={sortOption === MEMBERS_DIRECTORY_SORT.NAME_ASC}
          onPress={() => onSortOptionChange(MEMBERS_DIRECTORY_SORT.NAME_ASC)}
          testID={ACCOUNT_MEMBERS_DIRECTORY_TEST_IDS.SORT_NAME_ASC}
          disabled={disabled}
          styles={styles}
        />
        <DirectoryChip
          label={copy.MEMBERS_DIRECTORY_SORT_NAME_DESC}
          active={sortOption === MEMBERS_DIRECTORY_SORT.NAME_DESC}
          onPress={() => onSortOptionChange(MEMBERS_DIRECTORY_SORT.NAME_DESC)}
          testID={ACCOUNT_MEMBERS_DIRECTORY_TEST_IDS.SORT_NAME_DESC}
          disabled={disabled}
          styles={styles}
        />
        <DirectoryChip
          label={copy.MEMBERS_DIRECTORY_SORT_JOINED_NEWEST}
          active={sortOption === MEMBERS_DIRECTORY_SORT.JOINED_NEWEST}
          onPress={() => onSortOptionChange(MEMBERS_DIRECTORY_SORT.JOINED_NEWEST)}
          testID={ACCOUNT_MEMBERS_DIRECTORY_TEST_IDS.SORT_JOINED_NEWEST}
          disabled={disabled}
          styles={styles}
        />
        <DirectoryChip
          label={copy.MEMBERS_DIRECTORY_SORT_JOINED_OLDEST}
          active={sortOption === MEMBERS_DIRECTORY_SORT.JOINED_OLDEST}
          onPress={() => onSortOptionChange(MEMBERS_DIRECTORY_SORT.JOINED_OLDEST)}
          testID={ACCOUNT_MEMBERS_DIRECTORY_TEST_IDS.SORT_JOINED_OLDEST}
          disabled={disabled}
          styles={styles}
        />
      </View>

      {showResultsSummary ? (
        <Text
          testID={ACCOUNT_MEMBERS_DIRECTORY_TEST_IDS.RESULTS_SUMMARY}
          style={styles.resultsSummary}
        >
          {copy.membersDirectoryShowingCount(shownCount, totalCount)}
        </Text>
      ) : null}
    </View>
  )
}
